import { t } from 'i18next';
import { useEffect, useRef } from 'react';
import { useLoadGoogleMaps } from '../../../hooks/useLoadGoogleMaps.tsx';


const AddressLineClientForm = ({
  setValue,
  fields,
  setDisabled,
  disabled,
  country,
  id,
  register,
  errors
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const selectionMadeRef = useRef(false);
  const loaded = useLoadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
  const safeFields = Array.isArray(fields) ? fields : [];
  const extractAddressParts = (place) => {
    const components = place.address_components;
    const getComponent = (type, name = 'long_name') =>
      components.find((component) => component.types.includes(type))?.[name] || '';
    const level1 = getComponent('administrative_area_level_1');
    const city =
      getComponent('locality') ||
      getComponent('postal_town') ||
      getComponent('administrative_area_level_3');
    const country = getComponent('country');
    return {
      addressLine1: place?.name,
      city: city || level1,
      state: !city
        ? getComponent('administrative_area_level_2')
        : country === 'United Kingdom'
          ? getComponent('administrative_area_level_2')
          : getComponent('administrative_area_level_1') || '',
      postcode: getComponent('postal_code') + getComponent('postal_code_suffix'),
      country: getComponent('country', 'short_name')
    };
  };

  useEffect(() => {
    let listener = null;
    if (loaded && window.google?.maps?.places && inputRef.current ) {
       if (!sessionTokenRef.current) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode'],
        componentRestrictions: { country },
        fields: ['address_components', 'formatted_address','name'],
        sessionToken: sessionTokenRef.current
      });
      autocompleteRef.current = autocomplete;
      selectionMadeRef.current = false;

      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const address = extractAddressParts(place);
        selectionMadeRef.current = true;
        setDisabled(false);
        setValue(
          id,
          address?.addressLine1 || place?.formatted_address || place?.name || '',
          { shouldValidate: true, shouldDirty: true }
        );
        safeFields.find((val) => val?.config?.dbKey === 'address') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'address')?.id,
            address?.addressLine1
          );
        safeFields.find((val) => val?.config?.dbKey === 'registered_address') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'registered_address')?.id,
            address?.addressLine1
          );

        safeFields.find((val) => val?.config?.dbKey === 'address_line_2') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'address_line_2')?.id,
            address?.addressLine2
          );

        safeFields.find((val) => val?.config?.dbKey === 'city_state') &&
          setValue(safeFields.find((val) => val?.config?.dbKey === 'city_state')?.id, address?.city);
        safeFields.find((val) => val?.config?.dbKey === 'registered_city_state') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'registered_city_state')?.id,
            address?.city
          );

        safeFields.find((val) => val?.config?.dbKey === 'postcode') &&
          setValue(safeFields.find((val) => val?.config?.dbKey === 'postcode')?.id, address?.postcode);
        safeFields.find((val) => val?.config?.dbKey === 'registered_postcode') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'registered_postcode')?.id,
            address?.postcode
          );

        safeFields.find((val) => val?.config?.dbKey === 'county') &&
          setValue(safeFields.find((val) => val?.config?.dbKey === 'county')?.id, address?.state);
        safeFields.find((val) => val?.config?.dbKey === 'address_country') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'address_country')?.id,
            address?.country
          );
        safeFields.find((val) => val?.config?.dbKey === 'registered_country') &&
          setValue(
            safeFields.find((val) => val?.config?.dbKey === 'registered_country')?.id,
            address?.country
          );
      });
       sessionTokenRef.current = null;
    }
    return () => {
      window.google?.maps?.event?.removeListener(listener);
      if(autocompleteRef?.current){
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef?.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);
  useEffect(() => {
    setDisabled(true);
    selectionMadeRef.current = false;
  }, [setDisabled]);

  const addressFieldRegister = register(id, {
    validate: (value) => {
      if (disabled && (!value || value.trim() === '')) {
        return t('form.thisFieldRequired');
      }
      if (disabled && !selectionMadeRef.current) {
        return t('clientForm.selectAddressSuggestion');
      }
      return true;
    }
  });
  return (
    <div className="mb-2 flex w-full flex-col ">
      <div className="relative inline-block w-full">
        <label className="mb-2 block text-sm text-flow">
          {t("clientForm.startTypingAddress")}  <span className='notranslate'>{disabled && '*'}</span>
        </label>
        <input
          name={addressFieldRegister.name}
          onBlur={addressFieldRegister.onBlur}
          key={country}
          autoComplete="off"
          autoCorrect="off"
          disabled={!country}
          placeholder={t("clientForm.pleaseTypeYourAddress")}
          className={`flow-native-field mb-2${errors[id] ? ' flow-native-field--error' : ''}`}
          onChange={(e) => {
            selectionMadeRef.current = false;
            addressFieldRegister.onChange(e);
          }}
          ref={(e) => {
            addressFieldRegister.ref(e);
            inputRef.current = e;
          }}
        />
        {errors[id] && (
          <span className="text-flow-danger text-xxs">
            {errors[id]?.message || t('form.thisFieldRequired')}
          </span>
        )}
      </div>
      <div
        onClick={() => {
          if (country) {
            selectionMadeRef.current = false;
            setDisabled(false);
          }
        }}
        className={`text-end text-xs underline flow-upload-browse ${!country ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
      >
        {t("clientForm.enterAddressManually")}
      </div>
    </div>
  );
};
export default AddressLineClientForm;
